import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ReactZoomPanPinchProps,
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from "react-zoom-pan-pinch";
import { useSwiper } from "swiper/react";

import { EnlargeRefType } from "@is/components/EnlargeModal/EnlargeModal";
import { ENLARGED_PADDINGS, ENLARGED_SCALE_LEVELS } from "@is/constants/media";

import css from "./MediaZoomer.module.css";

const DEFAULT_ZOOM_OPTIONS: Partial<ReactZoomPanPinchProps> = {
  initialScale: ENLARGED_SCALE_LEVELS.MIN,
  maxScale: ENLARGED_SCALE_LEVELS.MAX,
  disablePadding: false,
  initialPositionX: 0,
  initialPositionY: 0,
  centerZoomedOut: true,
};

type Props = PropsWithChildren & {
  onClose: () => void;
  activeIndex: number;
  isClosing: boolean;
  enlargeRef: React.MutableRefObject<EnlargeRefType>;
};

export default function MediaZoomer({
  children,
  onClose,
  activeIndex,
  isClosing,
  enlargeRef,
}: Props) {
  const swiper = useSwiper();

  const canAutoSwitchSlideRef = useRef(false);
  const [currentScale, setCurrentScale] = useState(ENLARGED_SCALE_LEVELS.MIN);
  const [canPanning, setCanPanning] = useState(false);

  const options: Partial<ReactZoomPanPinchProps> = useMemo(
    () => ({
      ...DEFAULT_ZOOM_OPTIONS,
      panning: {
        disabled: canPanning,
      },
      ...(currentScale === ENLARGED_SCALE_LEVELS.MAX
        ? {
            doubleClick: {
              mode: "reset",
            },
          }
        : {}),
    }),
    [currentScale, canPanning]
  );

  const handleTransformed = useCallback(
    (ref) => {
      if (isClosing) {
        return;
      }

      const { state } = ref;
      const { scale } = state;

      if (scale <= ENLARGED_SCALE_LEVELS.TRIGGER_CLOSE) {
        onClose();
        return;
      }

      setCurrentScale(scale);
      if (enlargeRef) {
        enlargeRef.current = { scale, reset: ref.resetTransform };
      }
    },
    [onClose, isClosing, enlargeRef]
  );

  const handlePanningStart = useCallback((ref: ReactZoomPanPinchRef) => {
    const { state } = ref;
    const { scale } = state;

    if (scale === ENLARGED_SCALE_LEVELS.MIN) {
      return;
    }

    canAutoSwitchSlideRef.current = true;
  }, []);

  const handlePanning = useCallback(
    (ref: ReactZoomPanPinchRef) => {
      const { instance, state } = ref;
      const { bounds } = instance;
      const { minPositionX, maxPositionX } = bounds;
      const { positionX, scale } = state;

      if (
        scale === ENLARGED_SCALE_LEVELS.MIN ||
        canAutoSwitchSlideRef.current === false
      ) {
        canAutoSwitchSlideRef.current = false;
        return;
      }

      const roundedPositionX = Math.round(positionX);
      const roundedMinPositionX = Math.round(minPositionX);
      const roundedMaxPositionX = Math.round(maxPositionX);

      if (roundedPositionX === roundedMinPositionX - ENLARGED_PADDINGS) {
        canAutoSwitchSlideRef.current = false;
        swiper.slideNext();
      } else if (roundedPositionX === roundedMaxPositionX + ENLARGED_PADDINGS) {
        canAutoSwitchSlideRef.current = false;
        swiper.slidePrev();
      }
    },
    [swiper]
  );

  useEffect(() => {
    setCurrentScale(ENLARGED_SCALE_LEVELS.MIN);
  }, [activeIndex]);

  useEffect(() => {
    const delayToPanning = 100;

    const panningTimeout = setTimeout(() => {
      setCanPanning(currentScale === ENLARGED_SCALE_LEVELS.MIN);
    }, delayToPanning);

    return () => clearTimeout(panningTimeout);
  }, [currentScale]);

  return (
    <div className={css.transformWrapper}>
      <TransformWrapper
        {...options}
        onPanningStart={handlePanningStart}
        onPanning={handlePanning}
        onTransformed={handleTransformed}
      >
        <TransformComponent>{children}</TransformComponent>
      </TransformWrapper>
    </div>
  );
}
